import { ApiTokensCard } from "@/components/profile/api-tokens-card";
import { RadimWhatsappLinkCard } from "@/components/profile/radim-whatsapp-link-card";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LoadingLabel } from "@/components/ui/loading-label";
import { toast } from "@/components/ui/use-toast";
import { DiscordLinkField } from "@/components/users/discord-link-field";
import { SecondaryEmails } from "@/components/users/secondary-emails";
import { api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { Link, createFileRoute, useLoaderData, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

/**
 * `/profile` — first phase 6 page. Mirrors the Next-side
 * `src/app/(app)/profile/page.tsx`.
 *
 * Mutations toast on success/failure (shared `<Toaster>` mounted
 * in the `_app` layout) and call `router.invalidate()` (the
 * TanStack-router equivalent of Next's `router.refresh()`) so the
 * connected/disconnected card swaps in right after the round-trip
 * completes.
 */

type ProfileData = {
  id: string;
  name: string | null;
  email: string;
  discordUsername: string | null;
  /** Set only when the handle resolved to a real Discord account. */
  discordUserId: string | null;
  /** Null when the user signs in through GitLab. */
  password: { set: boolean } | null;
};

export const Route = createFileRoute("/_app/profile")({
  loader: () => api.get<ProfileData>("/api/me/profile"),
  component: ProfilePage,
});

function ProfilePage() {
  const profile = Route.useLoaderData();
  const { isCustomer } = useLoaderData({ from: "/_app" });

  if (isCustomer) {
    return (
      <div className="space-y-6">
        <ProfileHeading name={profile.name} email={profile.email} />
        {profile.password && <PasswordCard isSet={profile.password.set} />}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ProfileHeading name={profile.name} email={profile.email} />

      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Meeting identity</CardTitle>
            <CardDescription>
              How a recorded meeting knows a participant is you — and so whether it proposes your
              time at all on{" "}
              <Link to="/suggestions/time-entries" className="underline">
                Time suggestions
              </Link>
              . Discord matches on your account, not your name. Google Meet knows you only by email,
              so if you join on a different address than your Hub sign-in, add it below.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <DiscordLinkField
              userId={profile.id}
              initialHandle={profile.discordUsername}
              initialLinked={Boolean(profile.discordUserId)}
            />
            <div className="border-t pt-4">
              <SecondaryEmails userId={profile.id} primaryEmail={profile.email} />
            </div>
          </CardContent>
        </Card>

        <RadimWhatsappLinkCard />
        <BrowserTimerCard />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>API tokens</CardTitle>
          <CardDescription>
            Bearer credentials for your own machines — an automatic time tracker pushing proposed
            time entries into Hub, for instance. They aren't a login: each token reaches only the
            endpoints its scopes allow. Send one as{" "}
            <code className="text-xs">Authorization: Bearer …</code>. Proposals show up on{" "}
            <Link to="/suggestions/time-entries" className="underline">
              Time suggestions
            </Link>
            .
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ApiTokensCard />
        </CardContent>
      </Card>
    </div>
  );
}

function ProfileHeading({ name, email }: { name: string | null; email: string }) {
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold">Profile</h1>
      <p className="text-sm text-muted-foreground mt-1">
        {name} · {email}
      </p>
    </div>
  );
}

const PASSWORD_MIN_LENGTH = 10;

function PasswordCard({ isSet }: { isSet: boolean }) {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const save = useApiMutation(() => api.put("/api/auth/password", { password }), {
    onSuccess: () => {
      setPassword("");
      setConfirm("");
      toast({ title: "Password saved", variant: "success" });
      navigate({ to: "/dashboard" });
    },
  });
  const mismatch = confirm.length > 0 && confirm !== password;
  const valid = password.length >= PASSWORD_MIN_LENGTH && password === confirm;
  const inputClass =
    "mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

  return (
    <Card className="max-w-lg" data-testid="password-card">
      <CardHeader>
        <CardTitle>Password</CardTitle>
        <CardDescription>
          {isSet
            ? "You can sign in with your e-mail and password. Change it here; if you forget it, sign in with a link from e-mail and set a new one."
            : "Set a password to sign in without waiting for an e-mail. The sign-in link keeps working too."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid && !save.pending) save.mutate();
          }}
        >
          <label className="block">
            <span className="text-sm font-medium">{isSet ? "New password" : "Password"}</span>
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClass}
            />
            <span className="text-xs text-muted-foreground">
              At least {PASSWORD_MIN_LENGTH} characters.
            </span>
          </label>
          <label className="block">
            <span className="text-sm font-medium">Repeat it</span>
            <input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={inputClass}
            />
            {mismatch && <span className="text-xs text-destructive">The two don't match.</span>}
          </label>
          {save.error && <p className="text-xs text-destructive">{save.error}</p>}
          <div>
            <Button type="submit" size="sm" disabled={!valid || save.pending}>
              {save.pending ? (
                <LoadingLabel text="Saving" />
              ) : isSet ? (
                "Change password"
              ) : (
                "Set password"
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function BrowserTimerCard() {
  return (
    <Card data-testid="browser-timer-card">
      <CardHeader>
        <CardTitle>Timer in Chrome</CardTitle>
        <CardDescription>
          A toolbar button that starts and stops your Hub timer from any tab and shows how long it
          has run. It uses your Hub sign-in in this browser, nothing to paste.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <Button asChild>
          <a href="/install/hub-timer-extension.zip" download>
            Download the extension
          </a>
        </Button>
        <ol className="list-decimal space-y-1 pl-5 text-muted-foreground">
          <li>Unzip it into a folder you'll keep.</li>
          <li>
            Open <code className="text-xs">chrome://extensions</code> and turn on Developer mode.
          </li>
          <li>Click Load unpacked and pick that folder.</li>
          <li>Pin Netlion Hub Timer from the puzzle menu.</li>
        </ol>
        <p className="text-muted-foreground">
          To update, download it again, unzip over the same folder and click the reload arrow on the
          extension.
        </p>
      </CardContent>
    </Card>
  );
}
